import { createFileRoute, useRouter } from '@tanstack/react-router';
import { CalendarDays, Plus } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';

import { AdminOrgPicker } from '@apps/web/components/admin/admin_org_picker';
import { ShiftFormModal } from '@apps/web/components/admin/shift_form_modal';
import { resolveShiftStatus } from '@apps/web/components/dashboard/shift_list_filters';
import { AdminBadge } from '@apps/web/features/admin_design/admin_badge';
import {
    AdminButton,
    AdminSearchInput,
} from '@apps/web/features/admin_design/admin_controls';
import {
    AdminPage,
    AdminPageBody,
    AdminPageHeader,
} from '@apps/web/features/admin_design/admin_shell';
import { AdminTableSkeleton } from '@apps/web/features/admin_design/admin_skeleton';
import {
    AdminEmptyState,
    AdminErrorState,
} from '@apps/web/features/admin_design/admin_states';
import {
    AdminTable,
    AdminTableBody,
    AdminTableCell,
    AdminTableHeadCell,
    AdminTableHeader,
    AdminTableRow,
} from '@apps/web/features/admin_design/admin_table';
import { formatDateAndTimeWithTz } from '@apps/web/lib/date_utils';
import { getNavigableElementProps } from '@apps/web/lib/navigation_utils';
import { useAdminShifts } from '@apps/web/swr_data_fetching/admin_org_record_queries';

export const Route = createFileRoute('/_app/admin/shifts/')({
    head: () => ({
        meta: [
            {
                title: 'Shifts | Phoebe Admin',
            },
        ],
    }),
    validateSearch: z.object({
        organizationId: z.string().optional(),
    }),
    component: AdminShiftsPage,
});

const SHIFT_TABLE_COLUMNS = [
    'Patient',
    'Organization',
    'Start Time',
    'Role',
    'Caregiver',
    'Pay Rate',
    'Status',
] as const;

function AdminShiftsPage() {
    const router = useRouter();
    const { organizationId } = Route.useSearch();
    const [searchQuery, setSearchQuery] = useState('');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const {
        data: shiftsResponse,
        isLoading,
        error,
        hasRequestFailed,
        mutate: mutateShifts,
    } = useAdminShifts({
        page: 1,
        per_page: 100,
        ...(organizationId && { organization_id: organizationId }),
    });

    const shifts = shiftsResponse?.shifts ?? [];
    const hasLoadError =
        error !== undefined ||
        (hasRequestFailed && shiftsResponse === undefined);

    const filteredShifts = searchQuery
        ? shifts?.filter(
              (shift) =>
                  shift.patient_name
                      ?.toLowerCase()
                      .includes(searchQuery.toLowerCase()) ||
                  shift.organization_name
                      ?.toLowerCase()
                      .includes(searchQuery.toLowerCase()) ||
                  shift.assigned_caregiver_name
                      ?.toLowerCase()
                      .includes(searchQuery.toLowerCase()),
          )
        : shifts;

    const formatPayRate = (payRate?: number) => {
        if (!payRate) {
            return '—';
        }
        return `$${(payRate / 100).toFixed(2)}/hr`;
    };
    const formatStatus = (status: string) =>
        status
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (letter) => letter.toUpperCase());
    const getShiftNavigation = (shiftId: string) => {
        const routeOptions = {
            to: '/admin/shifts/$shift-id',
            params: { 'shift-id': shiftId },
        } as const;
        return {
            href: router.buildLocation(routeOptions).href,
            navigate: () => {
                void router.navigate(routeOptions);
            },
        };
    };

    return (
        <AdminPage>
            <AdminPageHeader
                title="Shifts"
                meta={
                    typeof shiftsResponse?.total === 'number'
                        ? `${shiftsResponse.total} in organization`
                        : `${shifts.length} most recent`
                }
                actions={
                    <AdminButton
                        onClick={() => setIsCreateModalOpen(true)}
                        variant="primary"
                    >
                        <Plus strokeWidth={1.75} />
                        Create Shift
                    </AdminButton>
                }
            >
                <AdminSearchInput
                    placeholder="Search shifts…"
                    containerClassName="max-w-md"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div className="flex items-center gap-2">
                    <span className="text-admin-sm text-admin-ink-muted font-medium">
                        Organization:
                    </span>
                    <AdminOrgPicker
                        triggerClassName="rounded-admin-sm border-admin-border text-admin-base h-8 w-[250px]"
                        value={organizationId}
                        onValueChange={(id) => {
                            void router.navigate({
                                to: '/admin/shifts',
                                search: (prev) => ({
                                    ...prev,
                                    organizationId: id,
                                }),
                            });
                        }}
                    />
                </div>
            </AdminPageHeader>
            <AdminPageBody>
                {isLoading ? (
                    <AdminTableSkeleton columns={SHIFT_TABLE_COLUMNS} />
                ) : hasLoadError ? (
                    <AdminErrorState
                        title="Failed to load shifts"
                        description="Please try again."
                        onRetry={() => {
                            void mutateShifts();
                        }}
                    />
                ) : filteredShifts?.length === 0 ? (
                    <AdminEmptyState
                        icon={CalendarDays}
                        title={
                            searchQuery ? 'No shifts found' : 'No shifts yet'
                        }
                        description={
                            searchQuery
                                ? 'Try a different name or clear the search.'
                                : 'Shifts appear here once an organization syncs.'
                        }
                    />
                ) : (
                    <AdminTable className="table-fixed">
                        <AdminTableHeader>
                            <tr>
                                <AdminTableHeadCell className="w-[20%]">
                                    Patient
                                </AdminTableHeadCell>
                                <AdminTableHeadCell className="w-[20%]">
                                    Organization
                                </AdminTableHeadCell>
                                <AdminTableHeadCell className="w-[15%]">
                                    Start Time
                                </AdminTableHeadCell>
                                <AdminTableHeadCell className="w-[10%]">
                                    Role
                                </AdminTableHeadCell>
                                <AdminTableHeadCell className="w-[15%]">
                                    Caregiver
                                </AdminTableHeadCell>
                                <AdminTableHeadCell
                                    className="w-[10%]"
                                    numeric={true}
                                >
                                    Pay Rate
                                </AdminTableHeadCell>
                                <AdminTableHeadCell className="w-[10%]">
                                    Status
                                </AdminTableHeadCell>
                            </tr>
                        </AdminTableHeader>
                        <AdminTableBody>
                            {filteredShifts.map((shift) => {
                                const displayStatus = resolveShiftStatus(
                                    shift.status,
                                    shift.latest_stage,
                                );
                                return (
                                    <AdminTableRow
                                        key={shift.id}
                                        className="cursor-pointer"
                                        {...getNavigableElementProps(
                                            getShiftNavigation(shift.id),
                                        )}
                                    >
                                        <AdminTableCell
                                            className="text-admin-ink truncate font-medium"
                                            title={shift.patient_name}
                                        >
                                            {shift.patient_name}
                                        </AdminTableCell>
                                        <AdminTableCell
                                            className="truncate"
                                            title={shift.organization_name}
                                        >
                                            {shift.organization_name}
                                        </AdminTableCell>
                                        <AdminTableCell
                                            className="truncate tabular-nums"
                                            title={formatDateAndTimeWithTz(
                                                shift.shift_start_time,
                                                shift.organization_timezone,
                                            )}
                                        >
                                            {formatDateAndTimeWithTz(
                                                shift.shift_start_time,
                                                shift.organization_timezone,
                                            )}
                                        </AdminTableCell>
                                        <AdminTableCell>
                                            <AdminBadge
                                                tone="outline"
                                                className="capitalize"
                                            >
                                                {shift.role_required || 'Any'}
                                            </AdminBadge>
                                        </AdminTableCell>
                                        <AdminTableCell
                                            className="truncate"
                                            title={
                                                shift.assigned_caregiver_name ||
                                                'Unassigned'
                                            }
                                        >
                                            {shift.assigned_caregiver_name || (
                                                <span className="text-admin-ink-faint">
                                                    Unassigned
                                                </span>
                                            )}
                                        </AdminTableCell>
                                        <AdminTableCell numeric={true}>
                                            {shift.pay_rate ? (
                                                formatPayRate(shift.pay_rate)
                                            ) : (
                                                <span className="text-admin-ink-faint">
                                                    —
                                                </span>
                                            )}
                                        </AdminTableCell>
                                        <AdminTableCell>
                                            <AdminBadge
                                                tone={
                                                    displayStatus === 'assigned'
                                                        ? 'ok'
                                                        : 'neutral'
                                                }
                                            >
                                                {formatStatus(displayStatus)}
                                            </AdminBadge>
                                        </AdminTableCell>
                                    </AdminTableRow>
                                );
                            })}
                        </AdminTableBody>
                    </AdminTable>
                )}
            </AdminPageBody>
            <ShiftFormModal
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                mode="create"
                onSaveSuccess={() => mutateShifts()}
            />
        </AdminPage>
    );
}
